import { ReactNode } from "react";

import { DEAL_DOC_ACCEPT_FORMATS, MAX_FILE_SIZE } from "@config/constants";
import { UPLOAD_ERROR_MESSAGES } from "@config/messages";

type UploadErrorKey = keyof typeof UPLOAD_ERROR_MESSAGES;

// Format → MIME mapping for file-upload validation. Add an entry here to make
// a new extension uploadable anywhere the upload helpers are used.
const UPLOAD_MIME_BY_FORMAT: Record<string, string> = {
  pdf:  "application/pdf",
  doc:  "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt:  "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls:  "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt:  "text/plain",
};

/**
 * MIME types implied by a list of bare formats (e.g. `["pdf", "docx"]`
 * → `{"application/pdf", "...wordprocessingml..."}`). Skips entries missing
 * from UPLOAD_MIME_BY_FORMAT.
 */
export const acceptedMimeTypes = (formats: readonly string[]): Set<string> =>
  new Set(
    formats.flatMap((f) => {
      const mime = UPLOAD_MIME_BY_FORMAT[f.toLowerCase()];
      return mime ? [mime] : [];
    })
  );

/**
 * Comma-separated, upper-case label for UI hints (e.g. `["pdf", "docx"]`
 * → `"PDF, DOCX"`).
 */
export const formatsToLabel = (formats: readonly string[]): string =>
  formats.map((f) => f.toUpperCase()).join(", ");

/**
 * Gets document error keys.
 *
 * @param file - The file needs to be validated.
 */
export function validateDocument(file: File): UploadErrorKey[] {
  const errors: UploadErrorKey[] = [];
  if (!acceptedMimeTypes(DEAL_DOC_ACCEPT_FORMATS).has(file.type)) {
    errors.push("IC_MEMO_CIM_FORMAT");
  }
  if (file.size > MAX_FILE_SIZE) {
    errors.push("SIZE_LIMIT_EXCEEDED");
  }

  return errors;
}

/**
 * Gets document error messages for multiple files.
 *
 * @param files - The files need to be validated.
 */
export function getErrorMessagesForDocuments(files: File[]): ReactNode[] {
  const errorKeys: UploadErrorKey[] = [];

  if (files.length > 1) {
    errorKeys.push("SINGLE_FILE_ONLY");
  }

  files.forEach((file) => errorKeys.push(...validateDocument(file)));

  // Deduplicate error messages (works with both strings and JSX nodes)
  return Array.from(
    new Set(errorKeys)).map((key) => UPLOAD_ERROR_MESSAGES[key]
  );
}